import type { SendingDomainWarmupStepDto } from '@/types/sendingDomains'
import { formatCount, utcDayLabel } from '@/utils/sendingDomainWarmup'

/** Where the domain is on the ladder: warming at a step, or stopped at one (or after Ready) with where it resumes. */
export type LadderPosition =
  | { kind: 'warming'; step: number }
  | { kind: 'stopped'; stoppedAtStep: number | null; resumeStep: number }

const CHIP = 'flex w-full justify-center rounded-md border px-0 py-1 text-xs font-medium tabular-nums'
const DONE = 'border-primary-700 bg-primary-700 text-white'
const CURRENT = 'border-primary-400 bg-primary-100 text-primary-800 dark:border-primary-600 dark:bg-primary-900/30 dark:text-primary-200'
const STOPPED = 'border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200'
const AHEAD = 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'

/**
 * "Limits by step": each step's daily and hourly limit, the steps behind the domain filled in, and where it is now.
 * The pattern of the broadcast ramp plan, as a table because each step has two limits to read against its column.
 */
export function WarmupLadder({ steps, position }: { steps: SendingDomainWarmupStepDto[]; position: LadderPosition }) {
  const current = position.kind === 'warming' ? position.step : position.resumeStep
  const stoppedAt = position.kind === 'stopped' ? position.stoppedAtStep : undefined
  // A domain that stopped after finishing warm-up stopped in the Ready column.
  const stoppedAfterReady = position.kind === 'stopped' && position.stoppedAtStep == null

  const marker = (step: SendingDomainWarmupStepDto) => {
    if (position.kind === 'warming') {
      if (step.Step === current) return <span className="block font-semibold text-primary-700 dark:text-primary-400">Today</span>
      if (step.Step < current && step.FirstDayUtc) return <span className="block text-slate-500 dark:text-slate-400">{utcDayLabel(step.FirstDayUtc)}</span>
      return null
    }
    if (step.Step === stoppedAt) return <span className="block font-semibold text-red-700 dark:text-red-400">Stopped</span>
    if (step.Step === current) return <span className="block font-semibold text-primary-700 dark:text-primary-400">Resumes</span>
    return null
  }

  const chipTone = (step: number) => {
    if (step === stoppedAt) return STOPPED
    if (step === current) return CURRENT
    return step < current ? DONE : AHEAD
  }

  const ariaCurrent = (step: number) => (position.kind === 'warming' ? step === current : step === stoppedAt) ? 'step' : undefined

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] table-fixed border-separate border-spacing-x-1 border-spacing-y-2">
        <thead>
          <tr>
            <th scope="col" className="w-16 text-left"><span className="sr-only">Limit</span></th>
            {steps.map(step => (
              <th key={step.Step} scope="col" className="align-top text-center text-[11px] font-medium text-slate-700 dark:text-slate-300">
                Step {step.Step}
                {marker(step)}
              </th>
            ))}
            <th scope="col" className="align-top text-center text-[11px] font-medium text-slate-700 dark:text-slate-300">
              Ready
              {stoppedAfterReady && <span className="block font-semibold text-red-700 dark:text-red-400">Stopped</span>}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" className="text-left text-xs font-medium text-slate-500 dark:text-slate-400">Per day</th>
            {steps.map(step => (
              <td key={step.Step}>
                <span className={`${CHIP} ${chipTone(step.Step)}`} aria-current={ariaCurrent(step.Step)}>{formatCount(step.DailyLimit)}</span>
              </td>
            ))}
            <td>
              <span className={`${CHIP} ${stoppedAfterReady ? STOPPED : AHEAD}`} aria-current={stoppedAfterReady ? 'step' : undefined}>No limit</span>
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-left text-xs font-medium text-slate-500 dark:text-slate-400">Per hour</th>
            {steps.map(step => (
              <td
                key={step.Step}
                className={`text-center text-xs tabular-nums ${step.Step === current
                  ? 'font-semibold text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400'}`}
              >
                {step.HourlyLimit != null ? formatCount(step.HourlyLimit) : 'no limit'}
              </td>
            ))}
            <td className="text-center text-xs text-slate-500 dark:text-slate-400">no limit</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
